<kendo-dialog
  (close)="onClose()"
  [width]="'720px'"
  [minWidth]="'400px'"
>
  <kendo-dialog-titlebar>
    <div class="dialog-title">
      <div class="score-badge">{{ scoreData().current_score }}</div>
      <div class="dialog-title__text">Your Identity Risk Score</div>
    </div>
  </kendo-dialog-titlebar>

  <!-- Summary sentence.

       Three clauses, each shown only when it has something to say, and each its own sentence:

         Your score reflects 67% lost across 6 active issues.
         Your score reflects 67% lost across 6 active issues. 1 risk accepted (not counted).
         Your score reflects 67% lost across 6 active issues. 1 risk accepted (not counted). 1 risk not evaluated.
         No active issues detected.

       Both trailing clauses are about the same thing: neither an accepted risk nor an
       unevaluated one is counted in the percentage above, which is why that number can look
       small while the lists further down are long. -->
  <p class="score-summary text-muted small mb-3">
    @if (failingChecks().length) {
      Your score reflects
      <span class="fw-semibold text-danger">{{ percentLost() }}%</span>
      lost across {{ failingChecks().length }} active
      issue{{ failingChecks().length === 1 ? '' : 's' }}.
    } @else {
      No active issues detected.
    }
    @if (acceptedChecks().length) {
      {{ acceptedChecks().length }} risk{{ acceptedChecks().length === 1 ? '' : 's' }}
      accepted (not counted).
    }
    @if (insufficientChecks().length) {
      {{ insufficientChecks().length }} risk{{ insufficientChecks().length === 1 ? '' : 's' }}
      not evaluated.
    }
  </p>

  <!-- Tier Legend: explain that issues are weighted by severity and tiers are adjustable -->
  <div class="tier-legend mb-3">
    <span class="tier-legend__label">Weighted by severity (most to least). Change a tier to fit your environment:</span>
    <div class="tier-legend__chips">
      @for (tier of tierOrder(); track tier) {
        <span class="tier-legend__chip" [class]="getSeverityClass(tier)">{{ tier }}</span>
      }
    </div>
  </div>

  <!-- All-Disabled Warning -->
  @if (allDisabled()) {
    <div class="alert alert-warning small mb-3" role="alert">
      All security checks are currently turned off, so this score doesn't reflect
      your actual posture. Turn checks back on to get an accurate reading.
    </div>
  }

  <div class="checks-list">
    <!-- Failing Checks (contribute to deduction) -->
    @if (failingChecks().length) {
      <div class="checks-group mb-3">
        <div class="checks-group__header d-flex align-items-center gap-2 mb-2">
          <span class="status-dot status-dot--fail"></span>
          <small class="fw-semibold">Issues lowering your score ({{ failingChecks().length }})</small>
        </div>
        @for (check of failingChecks(); track check.check_id) {
          <div class="check-row">
            <div class="check-row__inner">
              <span class="status-icon status-icon--fail">✗</span>
              <div class="check-body">
                <div class="check-body__head">
                  <span class="check-name fw-medium">{{ check.name }}</span>
                  <span class="check-deduction">−{{ check.score_impact }}%</span>
                </div>
                <a
                  class="manage-link"
                  [routerLink]="['/customer/itdr']"
                  [queryParams]="{ tab: 'posture', check: check.check_id }"
                  queryParamsHandling="merge"
                  (click)="onClose()"
                >
                  Manage this risk →
                </a>
                <div class="check-chips">
                  @if (check.severity) {
                    <select
                      class="tier-select"
                      [class]="getSeverityClass(check.severity)"
                      (change)="onTierChange(check.check_id, $any($event.target).value)"
                      title="Reassign severity tier"
                    >
                      @for (tier of severityTiers; track tier) {
                        <option [value]="tier" [selected]="tier === check.severity">{{ tier }}</option>
                      }
                    </select>
                  }
                  @if (check.is_overridden) {
                    <span class="override-hint"
                      [title]="'Changed from the default tier (' + check.default_severity + ')'">
                      Tier adjusted
                    </span>
                  }
                </div>
                <div class="check-detail text-muted small">{{ check.description }}</div>
                <div class="check-meta text-muted small">
                  <!-- Only entity-scoped checks rank individual identities. A tenant
                       check carries total_entities = 1 for its single setting, which
                       would otherwise render as "1 of 1 affected". -->
                  @if (check.scope === 'entity' && check.failed_entity_count != null && check.total_entity_count != null) {
                    <span>{{ check.failed_entity_count }} of {{ check.total_entity_count }} affected</span>
                  }
                  @if (check.detection_type) {
                    <span>Could enable: <span class="fw-medium">{{ check.detection_type }}</span></span>
                  }
                  @if (check.exploitation_likelihood) {
                    <span>Likelihood of attack: <span class="fw-medium">{{ check.exploitation_likelihood }}</span></span>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Accepted Checks -->
    @if (acceptedChecks().length) {
      <div class="checks-group mb-3">
        <div class="checks-group__header d-flex align-items-center gap-2 flex-wrap mb-2">
          <span class="status-dot status-dot--accepted"></span>
          <small class="fw-semibold">Risks you've accepted ({{ acceptedChecks().length }})</small>
          <small class="text-muted">acknowledged, not deducted from score</small>
        </div>
        @for (check of acceptedChecks(); track check.check_id) {
          <div class="check-row check-row--accepted">
            <div class="check-row__inner">
              <span class="status-icon status-icon--accepted">!</span>
              <div class="check-body">
                <div class="check-body__head">
                  <span class="check-name fw-medium">{{ check.name }}</span>
                  <span class="check-deduction check-deduction--muted">Risk accepted</span>
                </div>
                <a
                  class="manage-link"
                  [routerLink]="['/customer/itdr']"
                  [queryParams]="{ tab: 'posture', check: check.check_id }"
                  queryParamsHandling="merge"
                  (click)="onClose()"
                >
                  Review or reopen →
                </a>
                <div class="check-chips">
                  @if (check.severity) {
                    <select
                      class="tier-select"
                      [class]="getSeverityClass(check.severity)"
                      (change)="onTierChange(check.check_id, $any($event.target).value)"
                      title="Reassign severity tier"
                    >
                      @for (tier of severityTiers; track tier) {
                        <option [value]="tier" [selected]="tier === check.severity">{{ tier }}</option>
                      }
                    </select>
                  }
                  @if (check.is_overridden) {
                    <span class="override-hint"
                      [title]="'Changed from the default tier (' + check.default_severity + ')'">
                      Tier adjusted
                    </span>
                  }
                </div>
                <div class="check-detail text-muted small">{{ check.description }}</div>
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Passing Checks -->
    @if (passingChecks().length) {
      <div class="checks-group mb-3">
        <div class="checks-group__header d-flex align-items-center gap-2 mb-2">
          <span class="status-dot status-dot--pass"></span>
          <small class="fw-semibold">What's looking good ({{ passingChecks().length }})</small>
        </div>
        @for (check of passingChecks(); track check.check_id) {
          <div class="check-row">
            <div class="check-row__inner">
              <span class="status-icon status-icon--pass">✓</span>
              <div class="check-body">
                <div class="check-body__head">
                  <span class="check-name fw-medium">{{ check.name }}</span>
                </div>
                @if (check.severity) {
                  <div class="check-chips">
                    <select
                      class="tier-select"
                      [class]="getSeverityClass(check.severity)"
                      (change)="onTierChange(check.check_id, $any($event.target).value)"
                      title="Reassign severity tier"
                    >
                      @for (tier of severityTiers; track tier) {
                        <option [value]="tier" [selected]="tier === check.severity">{{ tier }}</option>
                      }
                    </select>
                    @if (check.is_overridden) {
                      <span class="override-hint"
                        [title]="'Changed from the default tier (' + check.default_severity + ')'">
                        Tier adjusted
                      </span>
                    }
                  </div>
                }
                <div class="check-detail text-muted small">{{ check.description }}</div>
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Not Evaluated Checks.

         Last section on purpose: these deducted nothing, so they belong after everything that
         moved the number. The caption says so, matching the accepted section directly above.

         Each row carries WHY it could not be evaluated, and there are exactly two reasons. The
         tier is one of them; a denied Graph permission is the other, and it is by far the more
         common in production. Telling them apart matters because only one of the two is
         something the customer can buy: sending someone to purchase a licence they already hold
         is the failure mode the pipeline spent a week removing, and this is where that
         distinction finally reaches the screen. -->
    @if (insufficientChecks().length) {
      <div class="checks-group">
        <div class="checks-group__header d-flex align-items-center gap-2 flex-wrap mb-2">
          <span class="status-dot status-dot--insufficient"></span>
          <small class="fw-semibold">Not evaluated ({{ insufficientChecks().length }})</small>
          <small class="text-muted">excluded from the score</small>
        </div>
        @for (check of insufficientChecks(); track check.check_id) {
          <div class="check-row check-row--not-evaluated">
            <div class="check-row__inner">
              <span class="status-icon status-icon--insufficient">?</span>
              <div class="check-body">
                <div class="check-body__head">
                  <span class="check-name fw-medium">{{ check.name }}</span>
                  <span class="check-deduction check-deduction--muted">Not evaluated</span>
                </div>
                <div class="check-detail text-muted small">{{ check.description }}</div>
                <div class="check-meta check-meta--reason small">{{ notEvaluatedReason(check) }}</div>
              </div>
            </div>
          </div>
        }
      </div>
    }
  </div>
</kendo-dialog>
